import { defineComponent, ref } from 'vue';
import AiGridVirtual from '@/components/ai-grid-virtual/ai-grid-virtual.vue';
import { setActiveViewInfo, setViewData } from '@/layouts/main-layout/main-layout-data';
import { svgAiLibrary } from '@/service/svg-ai-library';

export const componentGenerator = (metaArray, vueApp) => {
    for (const meta of metaArray) {
        let component = null;
        if (meta.type === undefined) {
            throw new Error('2e7edbcb-e1e8-45d2-8573-2d3073552911');
        }
        switch (meta.type) {
            case 'ai-grid-ro':
                component = aiGridRoGenerator(meta);
                vueApp.component(meta.name, component.component);
                break;
            case 'ai-grid-edit':
                component = aiGridEditGenerator(meta);
                vueApp.component(meta.name, component.component);
                break;
            case 'ai-grid-virtual':
                component = aiGridVirtualGenerator(meta);
                vueApp.component(meta.name, component.component);
                break;
            case 'ai-tree-list-edit':
                component = aiTreeListEditGenerator(meta);
                vueApp.component(meta.name, component.component);
                break;
            case 'ai-svg':
                component = aiSvgGenerator(meta);
                vueApp.component(meta.name, component.component);
                break;
            default:

            // throw new Error('38fa2d74-45e5-4df5-b7f3-b174992406bb');
        }
    }
    return { result: true };
};

const aiSvgGenerator = ({ name, meta, args }) => {
    const template = atob(meta.template);
    svgAiLibrary[name] = template;
    const component = {
        id: name,
        component: {
            name,
            data: function () {
                return { meta, args };
            },
            template: `<template><div id='${name}' class='ai-svg' style='height: ${meta.height}; width: ${meta.width}' @click=onClick>${template}</div></template>`,
            setup(props, { emit }) {
                const onClick = event => {
                    emit('click', event);
                };
                return { onClick };
            },
        },
    };
    return component;
};

const aiGridRoGenerator = ({ name, meta, args }) => {
    const component = {
        id: name,
        component: {
            name,
            data: function () {
                return { meta, args };
            },
            props: {
                toolbarConfig: {
                    type: Object,
                    default: () => {},
                },
                action: {
                    type: Object,
                    default: () => {},
                },
                preselectedRows: {
                    type: Array,
                    default: () => [],
                },
                userSettingsEnabled: {
                    type: Boolean,
                    default: true,
                },
                showToolbar: {
                    type: Boolean,
                    default: true,
                },
                showFilterPanel: {
                    type: Boolean,
                    default: true,
                },
                showGridFooter: {
                    type: Boolean,
                    default: true,
                },
                showColumnHeaders: {
                    type: Boolean,
                    default: true,
                },
                rowClickCallBack: {
                    type: Function,
                    default: () => {},
                },
                meta: {
                    type: Object,
                    default: () => meta,
                },
            },
            template: `
        <template>
          <ai-grid-ro-wrapper
            height='100%'
            :meta='meta'
            :action='action'
            :toolbarConfig='toolbarConfig'
            :preselectedRows='preselectedRows'
            :showToolbar='showToolbar'
            :userSettingsEnabled='userSettingsEnabled'
            :showFilterPanel='showFilterPanel'
            :showGridFooter='showGridFooter'
            :showColumnHeaders='showColumnHeaders'
            :args='args'
            :rowClickCallBack='rowClickCallBack'
            ref="componentRef"
            @user-settings-changed="onUserSettingsChanged"
            @user-settings-reset="onUserSettingsReset"
            @grid-ro-focus-changed="onGridRoFocusChanged"
            @dataLoaded="onDataLoadedHandler"
          />
        </template>
        `,
            // eslint-disable-next-line
            setup(props, { emit }) {
                const componentRef = ref();

                const onDataLoadedHandler = () => {
                    emit('dataLoaded');
                    console.log('onDataLoadedHandler');
                };

                const onUserSettingsChanged = event => {
                    emit('userSettingsChanged', event);
                };

                const onUserSettingsReset = () => {
                    emit('userSettingsReset');
                };

                const onGridRoFocusChanged = (row, viewId, rowIndex, component) => {
                    emit('gridRoFocusChanged', row, viewId, rowIndex, component);
                };

                return {
                    onDataLoadedHandler,
                    onUserSettingsChanged,
                    onUserSettingsReset,
                    onGridRoFocusChanged,
                    componentRef,
                };
            },
        },
    };
    return component;
};
const aiGridEditGenerator = ({ name, meta }) => {
    const component = {
        id: name,
        component: {
            name,
            data: function () {
                return { meta };
            },
            props: {
                action: {
                    type: Object,
                    default: () => {},
                },
                args: {
                    type: Object,
                    default: () => {},
                },
            },
            template: `
            <ai-grid-edit-wrapper height='100%'
            ref="componentRef"
            :meta='meta'
            :args='args'
            :action='action'
           @user-settings-changed="onUserSettingsChanged"
           @grid-focus-changed="onGridFocusChanged"
           @user-data-changed="onUserDataChanged"
           @refresh="onRefreshHandler"
           @dataLoaded="onDataLoadedHandler"
           @gridEditWrapperMounted="onGridEditWrapperMounted"
           @changesSavedSuccessfully="onChangesSavedSuccessfully"
           @changesRemoved="onChangesRemoved"
           @userDataChangedLoaded="onUserDataChangedLoaded"
           />
        `,
            // eslint-disable-next-line
            setup(props, { emit }) {
                const componentRef = ref();

                const onChangesRemoved = () => {
                    emit('changesRemoved');
                };

                const onDataLoadedHandler = () => {
                    emit('dataLoaded');
                };

                const onChangesSavedSuccessfully = () => {
                    emit('changesSavedSuccessfully');
                };

                const onUserDataChangedLoaded = userData => {
                    emit('userDataChangedLoaded', userData);
                };

                const onGridEditWrapperMounted = async () => {
                    if (meta.isVirtual) {
                        setActiveViewInfo({
                            refresh: componentRef.value.ref.setDataSource,
                            userData: () => {},
                            userSettings: () => {},
                        });

                        await setViewData();
                    }

                    emit('gridEditWrapperMounted');
                };

                const onRefreshHandler = () => {
                    emit('refresh');
                };

                const onUserDataChanged = args => {
                    emit('userDataChanged', args);
                };
                const onGridFocusChanged = (row, viewId, rowIndex, component) => {
                    emit('gridFocusChanged', row, viewId, rowIndex, component);
                };
                const onUserSettingsChanged = (settings, autoSaveMode) => {
                    emit('userSettingsChanged', settings, autoSaveMode);
                };

                return {
                    onDataLoadedHandler,
                    onRefreshHandler,
                    onUserDataChanged,
                    onGridFocusChanged,
                    onUserSettingsChanged,
                    onGridEditWrapperMounted,
                    onChangesSavedSuccessfully,
                    onChangesRemoved,
                    onUserDataChangedLoaded,
                    componentRef,
                };
            },
        },
    };
    return component;
};
const aiGridVirtualGenerator = ({ name, meta, args }) => {
    const component = {
        id: name,
        component: {
            name,
            data: function () {
                return { meta, args };
            },
            props: {
                findText: {
                    type: String,
                    default: () => {
                        return '';
                    },
                },
                toolbarShow: {
                    type: Boolean,
                    default: true,
                },
                filterPanelShow: {
                    type: Boolean,
                    default: () => {
                        return undefined;
                    },
                },
                selectionMode: {
                    type: String,
                },
                totalCountSelectedShow: {
                    type: Boolean,
                    default: () => true,
                },
                filterRow: {
                    type: Object,
                    default: () => {
                        return undefined;
                    },
                },
            },
            template: `
        <template>
          <ai-grid-virtual
            height='100%'
            :meta='meta'
            :findText='findText'
            :toolbarShow='toolbarShow'
            :filterPanelShow='filterPanelShow'
            :selectionMode='selectionMode'
            :totalCountSelectedShow='totalCountSelectedShow'
            :filterRow='filterRow'
          />
        </template>
        `,
        },
    };
    return component;
};
const aiTreeListEditGenerator = ({ name, meta, args }) => {
    const component = {
        id: name,
        component: {
            name,
            data: function () {
                return { meta, args };
            },
            props: {
                action: {
                    type: Object,
                    default: () => {},
                },
            },
            template: `
            <ai-tree-list-edit-wrapper
            ref="componentRef"
            :action='action'
            height='100%' :meta='meta'
            @tree-list-focus-changed="onTreeListFocusChanged"
            @dataLoaded="onDataLoadedHandler"
            @treeListEditWrapperMounted="onTreeListEditWrapperMounted"
            @changesSavedSuccessfully="onChangesSavedSuccessfully"
            @user-data-changed="onUserDataChanged"
            @refresh="onRefreshHandler"
            :toRemoveToolbarItemsNames="meta.toRemoveToolbarItemsNames"
            :toolbarUserItems="meta.toolbarUserItems"
            :showGridFooter="true"/>

        `,
            setup(props, { emit }) {
                const componentRef = ref();

                const onRefreshHandler = () => {
                    emit('refresh');
                };

                const onDataLoadedHandler = () => {
                    emit('dataLoaded');
                };

                const onUserDataChanged = args => {
                    emit('userDataChanged', args);
                };

                const onChangesSavedSuccessfully = () => {
                    emit('changesSavedSuccessfully');
                };

                const onTreeListEditWrapperMounted = () => {
                    emit('treeListEditWrapperMounted');
                };

                const onTreeListFocusChanged = (row, viewId, rowIndex, component) => {
                    emit('treeListFocusChanged', row, viewId, rowIndex, component);
                };
                return {
                    onChangesSavedSuccessfully,
                    onTreeListEditWrapperMounted,
                    onTreeListFocusChanged,
                    onDataLoadedHandler,
                    onUserDataChanged,
                    onRefreshHandler,
                    componentRef,
                };
            },
        },
    };
    return component;
};

export default defineComponent({
    components: { AiGridVirtual },
});
